//#region Imports
import React, { useState, useEffect } from "react";
import { StyleSheet, Platform, FlatList, SafeAreaView, ScrollView, View, useWindowDimensions } from "react-native";
import { useTheme, useNavigation } from '@react-navigation/native';
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomButton from "../../../components/CustomButton";
import { createCommonStyles } from '../../../styles/commonStyles';
import { useTranslation } from "react-i18next";
import Modal from "react-native-modal";
import { formatDateForUI } from "../../../constants/FormatDate";

const MyTrainingsScreen = ({
    userTrainings,
    setTRNUI,
    setIsNew,
    selectMyTrainingsDetailsScreen,
    isAdmin = false
}) => {
    //#region Const Definitions
    const { width, height } = useWindowDimensions();
    const { t } = useTranslation();
    const navigation = useNavigation();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height);

    const [trnData, setTrnData] = useState([]);
    const [filteredTrainings, setFilteredTrainings] = useState([]);
    const [isFilterModalVisible, setFilterModalVisible] = useState(false);

    const [filterAccepted, setFilterAccepted] = useState('All');

    //#region UseEffect
    useEffect(() => {
        setTrnData(userTrainings || []);
        setFilteredTrainings(userTrainings || []);
    }, [userTrainings]);

    useEffect(() => {
        let filtered = trnData;

        if (filterAccepted !== 'All') {
            filtered = filtered.filter(item => item.accepted === (filterAccepted === 'YES'));
        }

        setFilteredTrainings(filtered);
    }, [filterAccepted, trnData]);
    //#endregion

    const yesNoValue = (value) => value ? 'YES' : 'NO';

    //#region Render Functions
    const TrainingListView = ({ item }) => {
        return (
            <View style={common.cardstyle}>
                <SafeAreaView style={styles.row}>
                    <SafeAreaView style={{ flex: 4 }}>
                        <CustomLabel value={item.courseName} type="AVAILABLEUSED" />
                    </SafeAreaView>
                    <View style={styles.verticalLine} />

                    <SafeAreaView style={{ flex: 2.5 }}>
                        <CustomLabel value={formatDateForUI(item.startDate)} type="AVAILABLEUSED_SECONDARY" />
                    </SafeAreaView>
                    <View style={styles.verticalLine} />

                    <SafeAreaView style={{ flex: 2.5 }}>
                        <CustomLabel value={formatDateForUI(item.endDate)} type="AVAILABLEUSED_SECONDARY" />
                    </SafeAreaView>

                    {!isAdmin && (
                        <>
                            <View style={styles.verticalLine} />
                            <SafeAreaView style={{ flex: 1.5 }}>
                                <CustomLabel value={yesNoValue(item.userEditable)} type="AVAILABLEUSED_SECONDARY" />
                            </SafeAreaView>
                        </>
                    )}

                    <View style={styles.verticalLine} />
                    <SafeAreaView style={{ flex: 1.5 }}>
                        <CustomLabel value={yesNoValue(item.accepted)} type="AVAILABLEUSED_SECONDARY" />
                    </SafeAreaView>
                        

                    {isAdmin && (
                        <>
                            <View style={styles.verticalLine} />
                            <SafeAreaView style={{ flex: 1.8 }}>
                                <CustomLabel value={yesNoValue(item.isFacilitator)} type="AVAILABLEUSED_SECONDARY" />
                            </SafeAreaView>

                            <View style={styles.verticalLine} />
                            <SafeAreaView style={{ flex: 1.8 }}>
                                <CustomLabel value={yesNoValue(item.firstevaluation)} type="AVAILABLEUSED_SECONDARY" />
                            </SafeAreaView>

                            <View style={styles.verticalLine} />
                            <SafeAreaView style={{ flex: 1.8 }}>
                                <CustomLabel value={yesNoValue(item.secondevaluation)} type="AVAILABLEUSED_SECONDARY" />
                            </SafeAreaView>

                            <View style={styles.verticalLine} />
                            <SafeAreaView style={{ flex: 3 }}>
                                <CustomLabel value={item.organisation || ""} type="AVAILABLEUSED_SECONDARY" />
                            </SafeAreaView>
                        </>
                    )}

                    <View style={styles.verticalLine} />
                    <SafeAreaView style={{ flex: 2.5, paddingHorizontal: 10 }}>
                        <CustomButton
                            text={item.accepted ? t('common:accepted') : t('common:pendingAcceptance')}
                            onPress={() => {
                                setIsNew(false);
                                setTRNUI(item.trainingEmplUI);
                                selectMyTrainingsDetailsScreen();
                            }}
                            type={item.accepted ? "COMPLETE" : "PENDING"}
                            show="HIDE"
                        />
                    </SafeAreaView>
                </SafeAreaView>
            </View>
        );
    };

    const renderTraining = ({ item }) => <TrainingListView item={item} />;

    const ListHeader = () => (
        <SafeAreaView style={common.listTitle}>
            <SafeAreaView style={styles.rowHeaders}>
                <SafeAreaView style={{ flex: 4 }}>
                    <CustomLabel value={t('common:courseName')} type='SUBTITLE' />
                </SafeAreaView>

                <SafeAreaView style={{ flex: 2.5 }}>
                    <CustomLabel value={t('common:startDate')} type='SUBTITLE' />
                </SafeAreaView>

                <SafeAreaView style={{ flex: 2.5 }}>
                    <CustomLabel value={t('common:endDate')} type='SUBTITLE' />
                </SafeAreaView>

                {!isAdmin && (
                    <>
                        <SafeAreaView style={{ flex: 1.5 }}>
                            <CustomLabel value={t('common:userEditable')} type='SUBTITLE' />
                        </SafeAreaView>
                    </>
                )}
                <SafeAreaView style={{ flex: 1.5 }}>
                    <CustomLabel value={t('common:isAccepted')} type='SUBTITLE' />
                </SafeAreaView>

                {isAdmin && (
                    <>
                        <SafeAreaView style={{ flex: 1.8 }}>
                            <CustomLabel value={t('common:isFacilitator')} type='SUBTITLE' />
                        </SafeAreaView>
                        <SafeAreaView style={{ flex: 1.8 }}>
                            <CustomLabel value={t('common:firstEvaluation')} type='SUBTITLE' />
                        </SafeAreaView>
                        <SafeAreaView style={{ flex: 1.8 }}>
                            <CustomLabel value={t('common:secondEvaluation')} type='SUBTITLE' />
                        </SafeAreaView>
                        <SafeAreaView style={{ flex: 3 }}>
                            <CustomLabel value={t('common:trainingProvider')} type='SUBTITLE' />
                        </SafeAreaView>
                    </>
                )}

                <SafeAreaView style={{ flex: 2.5 }}>
                    <CustomLabel value={t('common:status')} type='SUBTITLE' />
                </SafeAreaView>
            </SafeAreaView>
            <SafeAreaView style={common.divider} />
        </SafeAreaView>
    );

    //#region Filter Panel
    const FilterPanel = () => {
        return (
            <SafeAreaView>
                <View style={[styles.inputContainer, { zIndex: 20 }]}>
                    <CustomLabel value={t('common:filterAccepted')} type="SUBTITLE" />
                    <View style={{ flexDirection: 'column', justifyContent: 'space-around', marginVertical: 10 }}>
                        {['All', 'YES', 'NO'].map(val => (
                            <CustomButton
                                key={val}
                                text={val}
                                type={filterAccepted === val ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
                                onPress={() => setFilterAccepted(val)}
                            />
                        ))}
                    </View>
                </View>
            </SafeAreaView>
        );
    };
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <CustomLabel
                value={t('navigate:MyTRN')}
                type='MINORTITLE'
            />

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 10, paddingTop: 10 }}>
                <CustomButton
                    iconName='funnel-outline'
                    show='BORDERLESS_FIXEDWIDTH'
                    textTop='2'
                    sz={25}
                    type="BORDERLESS_FIXEDWIDTH"
                    onPress={() => setFilterModalVisible(true)}
                />

                {!isAdmin && process.env.REACT_APP_CLIENT !== 'atlas' && (
                    <CustomButton
                        onPress={() => {
                            setIsNew(true);
                            selectMyTrainingsDetailsScreen();
                        }}
                        text={t('common:new')}
                        iconName='add-outline'
                        show='BORDERLESS_FIXEDWIDTH'
                        type='BORDERLESS_FIXEDWIDTH'
                        sz={25}
                    />
                )}
            </View>

            <Modal
                isVisible={isFilterModalVisible}
                hasBackdrop={true}
                backdropColor={colors.background_approved}
                backdropOpacity={0.9}
                onBackdropPress={() => setFilterModalVisible(false)}
                onRequestClose={() => setFilterModalVisible(false)}
            >
                <View style={{
                    width: (Platform.OS === 'web' && width > 1200) ? width / 4 : '95%',
                    alignSelf: 'center',
                    borderRadius: 10,
                    padding: 20,
                }}>
                    <FilterPanel />
                    <SafeAreaView>
                        <CustomButton
                            type="BORDERLESS"
                            text={t('navigate:cancel')}
                            onPress={() => setFilterModalVisible(false)}
                        />
                        <CustomButton
                            type="BORDERLESS"
                            text={t('navigate:filter')}
                            onPress={() => { setFilterModalVisible(false); }}
                        />
                    </SafeAreaView>
                </View>
            </Modal>

            <ScrollView style={styles.listWrapper}>
                <FlatList
                    data={filteredTrainings}
                    keyExtractor={(item, index) => item.trainingEmplUI || `${index}`}
                    renderItem={renderTraining}
                    ListHeaderComponent={ListHeader}
                    stickyHeaderIndices={[0]}
                    contentContainerStyle={{ paddingBottom: 20 }}
                />
            </ScrollView>
        </SafeAreaView>
    );
}

const makeStyles = (colors, height, width) => StyleSheet.create({
    mainView: {
        flex: 1,
        height: Platform.OS === "web" ? (height * 60) / 100 : "95%",
        justifyContent: "center",
    },
    inputContainer: {
        marginTop: 10,
        marginBottom: 20,
    },
    verticalLine: {
        backgroundColor: colors.buttons_primary,
        height: '100%',
        width: 1,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        minHeight: 50,
    },
    listHeader: {
        backgroundColor: colors.background_card,
        borderBottomWidth: 1,
        borderBottomColor: colors.buttons_primary,
    },
    button: {
        flex: 1,
        marginHorizontal: Platform.OS === 'web' ? 0 : 1,
        justifyContent: "center",
        alignSelf: "center",
        alignItems: "center",
    },
    listTitle: {
        flex: 1,
        marginVertical: 10,
    },
    rowHeaders: {
        flexDirection: "row",
        marginHorizontal: '1%',
    },
});

export default MyTrainingsScreen;